import React, {useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {Paragraph} from '../../components/ui-controls/text/Paragraph';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName} from '../../utils/analytics/events';
import {logger, serializeError} from '../../utils/logger';

const AccountDeleteScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_DELETE>) => {
    const {userStore, shopSelectionStore} = useStores();
    const isLoggedIn = userStore.isLoggedIn;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDeleteAccount = async () => {
      setIsSubmitting(true);
      try {
        const userId = userStore.id;

        await userStore.deleteUser();
        await userStore.logout();

        Analytics.logEvent({
          eventName: EventName.DeleteAccount,
          parameters: {
            user_id: userId,
          },
        });
        Analytics.clearUserSession();
        navigation.navigate(shopSelectionStore.homeAppRoute);
      } catch (err) {
        logger.error('AccountDeleteScreen', 'Error deleting user', serializeError(err));
      } finally {
        setIsSubmitting(false);
      }
    };
    const handleCancel = () => navigation.goBack();
    const handleLogin = () => navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);

    return (
      <ContentView>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <Text style={styles.heading}>We&apos;re sorry to see you go</Text>
            <Text style={styles.paragraph}>
              Deleting your account will remove your shopping history, wishlist, and communication
              preferences from the app and web store.
            </Text>
            <Text style={styles.paragraph}>
              After you submit this request, your account will be suspended and queued for deletion
              and you will no longer have access or be able to reactivate your account.
            </Text>
            <Text style={styles.paragraph}>
              If you just want to change how often you hear from us you can turn off app
              notifications or adjust your email and SMS communication preferences in your account
              settings.
            </Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.heading}>What else should you know</Text>
            <Text style={styles.paragraph}>
              Once you delete your account, we will no longer collect or use your personal data.
              Some account information may be retained for fraud, product recall, compliance,
              regulatory and other legal purposes.
            </Text>
            <Text style={styles.paragraph}>
              Deleting your account will unsubscribe you from marketing emails. This may take 1-2
              weeks to take effect.
            </Text>
            <Text style={styles.paragraph}>
              The full privacy policy is available for review at any time.
            </Text>
            <Text style={styles.paragraph}>
              If you&apos;re sure you&apos;d like to permanently delete your account, click on
              “Delete my account” below.
            </Text>
          </View>
          {isLoggedIn ? (
            <View style={styles.buttonsContainer}>
              <Button
                onPress={handleDeleteAccount}
                title="DELETE MY ACCOUNT"
                buttonStyle={styles.button}
                disabled={isSubmitting}
                loading={isSubmitting}
              />
              <Button
                type="outline"
                onPress={handleCancel}
                title="NEVER MIND, KEEP MY ACCOUNT"
                buttonStyle={styles.button}
                disabled={isSubmitting}
              />
            </View>
          ) : (
            <>
              <Paragraph>
                You are not logged in. Please log in and use the &quot;Delete My Account&quot; menu
                option in &quot;My Account&quot; to delete your account.
              </Paragraph>
              <View style={styles.buttonsContainer}>
                <Button onPress={handleLogin} title="LOG IN" />
              </View>
            </>
          )}
        </ScrollView>
      </ContentView>
    );
  },
);

const styles = StyleSheet.create({
  heading: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 16,
    color: theme.colour.text.content.primary,
    marginBottom: 8,
  },
  section: {
    marginBottom: 16,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    marginBottom: 8,
    width: 300,
  },
});

export default AccountDeleteScreen;
